import fs from "node:fs/promises";
import path from "node:path";
import { fileURLToPath } from "node:url";
import { type ServerType, serve } from "@hono/node-server";
import { serveStatic } from "@hono/node-server/serve-static";
import { type FSWatcher, watch } from "chokidar";
import { Hono } from "hono";
import { createNodeFileSystem } from "./adapters/node-fs.js";
import { createNodeEditorPreferences } from "./adapters/node-preferences.js";
import {
  type AssetSource,
  createEditorApp,
  type EditorApp,
  type EditorFetch,
} from "./app.js";
import { editorConfig } from "./constants.js";
import type { CommonLogger } from "./interfaces.js";
import type { ChangeNotifier } from "./ports.js";

export interface LocalEditorOptions {
  sourceDirectory: string;
  port: number;
  logger: CommonLogger;
  deploymentUrl: string;
}

/**
 * Serves the built client out of `dist/client`.
 *
 * The directory is absent until the client has been built, which is the normal
 * state in development — Vite is in front then and serves the client itself. So
 * `index.html` is read on demand and a missing build simply reports no assets,
 * rather than pointing `serveStatic` at a root that isn't there.
 */
function createNodeAssets(clientDir: string): AssetSource {
  /**
   * `serveStatic` is Hono middleware and wants a Hono context, but `AssetSource`
   * hands over the request instead — see the interface for why. So it runs in a
   * one-middleware app of its own: `fetch` builds the context, which means a real
   * one rather than the synthesised context that doesn't work.
   *
   * With no handler after the middleware, the `next()` it calls on a miss lands
   * on Hono's default 404 — the same signal Cloudflare's assets binding gives the
   * Worker's implementation, and translated to `undefined` the same way.
   */
  const staticApp = new Hono().use("*", serveStatic({ root: clientDir }));
  const documentPath = path.join(clientDir, "index.html");

  /**
   * Cached once read, so a document request doesn't re-read the file and an
   * asset request doesn't read it at all beyond the first.
   *
   * Only success is cached. A missing build is rechecked every time, which is
   * what lets a server started before `build:client` finished pick the client up
   * without a restart — the previous behaviour read the template once at startup
   * and never noticed it appear.
   */
  let document: string | undefined;

  const readDocument = async (): Promise<string | undefined> => {
    if (document !== undefined) {
      return document;
    }
    try {
      document = await fs.readFile(documentPath, "utf-8");
      return document;
    } catch {
      return undefined;
    }
  };

  return {
    readDocument,

    async serve(request) {
      // No client build means no assets to serve, and pointing `serveStatic` at
      // a root that isn't there just makes it complain.
      if (!(await readDocument())) {
        return undefined;
      }
      const response = await staticApp.fetch(request);
      return response.status === 404 ? undefined : response;
    },
  };
}

/**
 * Reports changes under the project's watched directories via chokidar.
 */
function createChokidarNotifier(
  watcher: FSWatcher,
  sourceDirectory: string,
): ChangeNotifier {
  return {
    onChange(listener) {
      const handler = (filePath: string) => {
        listener(path.relative(sourceDirectory, filePath));
      };
      watcher.on("change", handler);
      return () => {
        watcher.off("change", handler);
      };
    },
  };
}

/**
 * The editor server as the Zuplo CLI runs it: a Node HTTP listener over the
 * shared Hono app, with the real filesystem, chokidar, and the built client on
 * disk plugged into it.
 */
export class LocalEditorServer {
  /**
   * Answers a request without going through the listener, for tests and for
   * anything embedding the editor in a server of its own.
   */
  public fetch: EditorFetch;
  private editorApp: EditorApp;
  private server: ServerType | undefined;
  private listenerHost: string;
  private watcher: FSWatcher;
  private listenerPort: number;
  private logger: CommonLogger;
  private sourceDirectory: string;

  constructor(options: LocalEditorOptions) {
    this.listenerPort = options.port;
    this.logger = options.logger;
    this.sourceDirectory = options.sourceDirectory;
    this.listenerHost = editorConfig.server.host;
    // Watch all project directories
    this.watcher = watch(
      editorConfig.watch.watchedDirs.map((dir) =>
        path.join(this.sourceDirectory, dir),
      ),
      { ignoreInitial: true },
    );

    // In development Vite is in front and serves the client itself, so the
    // directory is only wired up when a build is actually there.
    const clientDir = fileURLToPath(new URL("../dist/client", import.meta.url));

    this.editorApp = createEditorApp({
      fs: createNodeFileSystem(options.sourceDirectory),
      preferences: createNodeEditorPreferences(),
      assets: createNodeAssets(clientDir),
      changes: createChokidarNotifier(this.watcher, options.sourceDirectory),
      logger: options.logger,
      deploymentUrl: options.deploymentUrl,
      sourceDirectory: options.sourceDirectory,
    });

    this.fetch = this.editorApp.fetch;
  }

  init = async (): Promise<void> => {
    this.logger.info(
      { sourceDirectory: this.sourceDirectory },
      "Starting local editor server...",
    );
  };

  start = async (): Promise<void> => {
    await this.init();

    await new Promise<void>((resolve, reject) => {
      const onListenError = (err: Error) => reject(err);

      const server = serve(
        {
          fetch: this.fetch,
          port: this.listenerPort,
          hostname: this.listenerHost,
        },
        () => {
          // Later errors belong to the server, not to starting it up.
          server.off("error", onListenError);
          resolve();
        },
      );

      server.once("error", onListenError);
      this.server = server;
    });
  };

  close = async (): Promise<void> => {
    await this.editorApp.dispose();
    await this.watcher.close();

    const server = this.server;
    if (!server) {
      return;
    }
    this.server = undefined;

    // Drop keep-alive sockets that aren't serving a request, so the port is
    // free right away — the dev script restarts on the same port.
    if ("closeIdleConnections" in server) {
      server.closeIdleConnections();
    }

    await new Promise<void>((resolve, reject) => {
      server.close((err) => (err ? reject(err) : resolve()));
    });
  };
}
